import { canonicaliseWorkflow } from './canonicalise';
import { SeedWorkflowsError } from '../errors';
import type { WorkflowDefinition } from '../types';

export interface SeedWorkflowsOptions {
  workflows: WorkflowDefinition[];
  loomApiUrl: string;
  auth: { accessToken: string } | { apiKey: string };
  /**
   * Map of owner.slug → ownerOrgId. The loader resolves owner.slug
   * against Keep before seeding; for v0.1 the consuming service can
   * pre-resolve and pass a map (the substrate end-to-end Keep lookup
   * lands in the Sift contract pass).
   */
  ownerSlugToOrgId: Record<string, string>;
  env?: Record<string, string>;
  logger?: {
    info(msg: string, meta?: Record<string, unknown>): void;
    warn(msg: string, meta?: Record<string, unknown>): void;
    error(msg: string, meta?: Record<string, unknown>): void;
  };
  publish?: boolean;
  /** Override `fetch` (tests). */
  fetchFn?: typeof fetch;
}

export interface SeedWorkflowsResult {
  workflows: Array<{
    name: string;
    status: 'unchanged' | 'created_draft' | 'created_published' | 'skipped_locked';
    workflowId: string | null;
    versionId: string | null;
    versionNumber: number | null;
  }>;
}

const NULL_LOGGER = {
  info: () => {},
  warn: () => {},
  error: () => {},
};

export async function seedWorkflows(opts: SeedWorkflowsOptions): Promise<SeedWorkflowsResult> {
  const logger = opts.logger ?? NULL_LOGGER;
  const fetchFn = opts.fetchFn ?? fetch;
  const baseUrl = opts.loomApiUrl.replace(/\/$/, '');

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if ('accessToken' in opts.auth) {
    headers['Authorization'] = `Bearer ${opts.auth.accessToken}`;
  } else {
    headers['X-API-Key'] = opts.auth.apiKey;
  }

  const results: SeedWorkflowsResult['workflows'] = [];
  for (const workflow of opts.workflows) {
    const ownerOrgId = opts.ownerSlugToOrgId[workflow.owner.slug];
    if (ownerOrgId === undefined) {
      throw new SeedWorkflowsError(
        workflow.name,
        `seedWorkflows: owner.slug "${workflow.owner.slug}" not present in ownerSlugToOrgId map`,
      );
    }
    const opts2: import('./canonicalise').CanonicaliseOptions = { ownerOrgId };
    if (opts.env !== undefined) opts2.env = opts.env;
    if (opts.publish !== undefined) opts2.publish = opts.publish;
    const body = await canonicaliseWorkflow(workflow, opts2);

    logger.info(`seeding workflow ${workflow.name}`, {
      ownerOrgId,
      nodes: body.nodes.length,
      transitions: body.transitions.length,
    });

    const response = await fetchFn(`${baseUrl}/api/admin/seed-workflow`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const json = (await response.json()) as {
      success: boolean;
      data?: {
        name: string;
        status: 'unchanged' | 'created_draft' | 'created_published' | 'skipped_locked';
        workflowId: string | null;
        versionId: string | null;
        versionNumber: number | null;
      };
      error?: { code: string; message: string };
    };
    if (!response.ok || !json.success || !json.data) {
      const message = json.error?.message ?? `seedWorkflows: ${workflow.name} failed with HTTP ${response.status}`;
      throw new SeedWorkflowsError(workflow.name, message);
    }
    results.push(json.data);
  }
  return { workflows: results };
}
